import { useCallback, useRef, useState } from 'react';

import { createResizeObserverWithRAF } from '@heidi/utils';

/**
 * Measures an element's layout width and keeps it current. Returns a ref
 * callback to attach and the latest width (in px, `undefined` until measured).
 *
 * Uses `offsetWidth` — layout-based, so it's immune to CSS transforms (e.g. a
 * tooltip's entrance `zoom` scale, which would corrupt a `getBoundingClientRect`
 * read) and correct for inline elements. Deliberately not the `ResizeObserver`
 * entry's `borderBoxSize`: for an inline box that reads 0, so reading it would
 * clobber the real width to 0 (it's only valid for block/absolute boxes). A
 * `ResizeObserver` tracks later changes such as font loads; it's rAF-batched
 * (`createResizeObserverWithRAF`, the repo's T5 standard) so the `offsetWidth`
 * read + state update coalesce to one per frame rather than firing synchronously
 * per callback. The observer is guarded for environments without it (e.g. jsdom).
 */
export function useMeasuredWidth() {
  const [width, setWidth] = useState<number>();
  const observerRef = useRef<ResizeObserver>();

  const ref = useCallback((node: HTMLElement | null) => {
    observerRef.current?.disconnect();
    observerRef.current = undefined;
    if (!node) return;
    setWidth(node.offsetWidth);
    if (typeof ResizeObserver !== 'undefined') {
      observerRef.current = createResizeObserverWithRAF(() => setWidth(node.offsetWidth));
      observerRef.current.observe(node);
    }
  }, []);

  return [ref, width] as const;
}
